"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Menu } from "lucide-react"
import { BrandMark } from "@/components/monolith/brand-mark"
import { DAPP_LINKS } from "@/lib/dapp-content"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

/** Use sheet nav on phones and tablets — horizontal strip is hard to tap and crowds the chrome. */
const COMPACT_NAV_MAX_PX = 1024

export function Navigation() {
  const pathname = usePathname()
  const [menuOpen, setMenuOpen] = useState(false)
  const [compactNav, setCompactNav] = useState(false)

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${COMPACT_NAV_MAX_PX}px)`)
    const sync = () => setCompactNav(mql.matches)
    sync()
    mql.addEventListener("change", sync)
    return () => mql.removeEventListener("change", sync)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  const links = [
    { href: "/", label: "HOME" },
    { href: "/about", label: "ABOUT" },
    { href: "/roadmap", label: "ROADMAP" },
    { href: "/tokenomics", label: "TOKENOMICS" },
    { href: "/whitepaper", label: "WHITEPAPER" },
    { href: "/learn", label: "LEARN" },
    { href: "/faq", label: "FAQ" },
  ]

  return (
    <nav className={`navbar${compactNav ? " navbar--compact" : ""}`} aria-label="Primary">
      <Link href="/" className="brand" prefetch aria-label="Rivyu Protocol home" suppressHydrationWarning>
        <BrandMark variant="nav" />
        <span className="nav-brand-name">RIVYU PROTOCOL</span>
      </Link>

      {compactNav ? (
        <>
          <div className="navbar-compact-spacer" aria-hidden />
          <div className="navbar-compact-actions">
            <a
              className="nav-bot-cta nav-bot-cta--toolbar"
              href={DAPP_LINKS.telegramBot}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open Rivyu Protocol official bot on Telegram"
              suppressHydrationWarning
            >
              JOIN_BOT //
            </a>
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="nav-menu-trigger"
                  aria-expanded={menuOpen}
                  aria-controls="site-primary-nav-sheet"
                  aria-label="Open site menu"
                >
                  <Menu className="nav-menu-trigger-icon" aria-hidden strokeWidth={1.75} />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                id="site-primary-nav-sheet"
                className="nav-sheet-content border-[var(--line)] bg-[var(--bg)] text-[var(--text)] sm:max-w-sm"
              >
                <SheetHeader className="border-b border-[var(--line-2)] pb-4 text-left">
                  <SheetTitle asChild>
                    <p className="m-0 font-mono text-xs tracking-[0.28em] text-[var(--green-soft)] uppercase">
                      Navigate
                    </p>
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-2 flex flex-col gap-0.5 pr-2" aria-label="Site sections">
                  {links.map((link) => (
                    <SheetClose asChild key={link.href}>
                      <Link
                        href={link.href}
                        prefetch
                        className={pathname === link.href ? "nav-sheet-link active" : "nav-sheet-link"}
                        suppressHydrationWarning
                      >
                        {link.label}
                      </Link>
                    </SheetClose>
                  ))}
                </div>
                <div className="mt-6 border border-dashed border-[var(--line-2)] p-4">
                  <p className="mb-3 font-mono text-[10px] tracking-[0.22em] text-[var(--text-faint)] uppercase">
                    <span className="inline-flex items-center gap-2 text-[var(--testnet-orange)]">
                      <span className="nav-testnet-dot" aria-hidden />
                      Testnet
                    </span>
                  </p>
                  <SheetClose asChild>
                    <a
                      className="nav-bot-cta inline-flex w-full min-h-12 items-center justify-center text-center"
                      href={DAPP_LINKS.telegramBot}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open Rivyu Protocol official bot on Telegram"
                      suppressHydrationWarning
                    >
                      JOIN_BOT //
                    </a>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </>
      ) : (
        <>
          <div className="nav-links-scroll">
            {links.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                prefetch
                className={pathname === link.href ? "nav-link active" : "nav-link"}
                suppressHydrationWarning
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="navbar-cta">
            <span className="nav-testnet">
              <span className="nav-testnet-dot" aria-hidden />
              TESTNET
            </span>
            <a
              className="nav-bot-cta"
              href={DAPP_LINKS.telegramBot}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open Rivyu Protocol official bot on Telegram"
              suppressHydrationWarning
            >
              JOIN_BOT //
            </a>
          </div>
        </>
      )}
    </nav>
  )
}
